import { useEffect, useState }      from 'react';
import { useParams }                from 'react-router-dom';
import View                         from '@widesk-ui/components/View';
import Box                          from '@widesk-ui/components/Box';
import Title                        from '@widesk-ui/components/Title';
import Spin                         from '@widesk-ui/components/Spin';
import Alert                        from '@widesk-ui/components/Alert';
import Button                       from '@widesk-ui/components/Button';
import useTheme                     from '@widesk-ui/hooks/useTheme';
import useDidMount                  from '@widesk-core/hooks/useDidMount';
import { useAuth }                  from '@widesk-ui/hooks/useAuth';
import { navigate }                 from '@widesk-ui/hooks/useNavigate';
import getTextAndTranslateFromError from '@widesk-core/tools/getTextAndTranslateFromError';
import AcceptAccountInvitationModel from '@/models/services/account/AcceptAccountInvitationModel';
import useAccount                   from '@/hooks/useAccount';
import { getIdFromUrn }             from '@widesk-core/tools/identifier';
import { renewTokenUntil }          from '@/helpers/tokenRefresh';
import AccountModel                 from '@models/account/AccountModel';

type AcceptState = 'loading' | 'success' | 'error';

const canReadAccount = (id: id) => new AccountModel({ id }).fetch({ noError: true }).then(() => true, () => false);

export default function InvitationAcceptPage() {
	const theme = useTheme();
	const { token } = useParams();
	const { logout } = useAuth();
	const { selectAccount } = useAccount();

	const [state, setState] = useState<AcceptState>('loading');
	const [errorMessage, setErrorMessage] = useState('');
	const [errorAction, setErrorAction] = useState<'logout' | 'home'>('home');

	useDidMount(() => document.title = 'Rejoindre le compte');

	useEffect(() => {
		(async () => {
			try {
				const accept = new AcceptAccountInvitationModel();
				await accept.patch({ token }, { noError: true } as ConnectorSaveOptions);

				setState('success');

				const accountId = getIdFromUrn(accept.account);

				if (!await renewTokenUntil(() => canReadAccount(accountId))) {
					setErrorMessage("L'invitation est acceptée, mais l'accès au compte n'est pas encore prêt. Déconnectez-vous puis reconnectez-vous dans quelques instants.");
					setErrorAction('logout');
					setState('error');
					return;
				}

				await selectAccount(accountId);
				navigate('/');
			} catch (e: any) {
				const status = e?.connectorError?.response?.status;

				// 409 = déjà membre (ex. inscription via l'invitation) : succès silencieux.
				if (status === 409) {
					navigate('/');
					return;
				}

				const message = status === 403
					? "Cette invitation est associée à une autre adresse e-mail que celle de votre compte connecté. Déconnectez-vous puis reconnectez-vous avec le bon compte."
					: status === 404 || status === 410
						? "Ce lien d'invitation est invalide, a expiré ou a été révoqué."
						: (getTextAndTranslateFromError(e) || "Une erreur est survenue lors de l'acceptation de l'invitation.");

				setErrorMessage(message);
				setErrorAction(status === 403 ? 'logout' : 'home');
				setState('error');
			}
		})();
	}, []);

	return (
		<View heightF center gap="lg">
			<Box style={{ width: 460, padding: theme.marginLG }}>
				<View gap="xxl" center style={{ textAlign: 'center' }}>
					<Title size={theme.fontSizeHeading2}>Rejoindre le compte</Title>

					{state === 'loading' && (
						<Spin size="large" tip="Traitement de l'invitation" />
					)}

					{state === 'success' && (
						<>
							<Alert type="success" message="Invitation acceptée" description="Vous rejoignez le compte..." />
							<Spin size="large" />
						</>
					)}

					{state === 'error' && (
						<>
							<Alert type="error" message="Problème lors de l'acceptation" description={errorMessage} />
							{errorAction === 'logout' ? (
								<Button type="primary" onClick={() => logout()}>Se déconnecter</Button>
							) : (
								<Button type="primary" onClick={() => navigate('/')}>Retour à l'accueil</Button>
							)}
						</>
					)}
				</View>
			</Box>
		</View>
	);
}
